import { useState } from 'react';
import { User, Mail, Calendar, Shield, LogOut, Camera, Globe, Languages } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuthStore } from '@/stores/authStore';
import { useToast } from '@/hooks/useToast';
import { getInitials } from '@/lib/utils';
import { authService } from '@/services/authService';

// Region options
const REGIONS = [
  { value: 'Seoul', label: '서울 (Seoul)' },
  { value: 'Busan', label: '부산 (Busan)' },
  { value: 'Incheon', label: '인천 (Incheon)' },
  { value: 'Daegu', label: '대구 (Daegu)' },
  { value: 'Daejeon', label: '대전 (Daejeon)' },
  { value: 'Gwangju', label: '광주 (Gwangju)' },
  { value: 'Tokyo', label: '도쿄 (Tokyo)' },
  { value: 'New York', label: '뉴욕 (New York)' },
  { value: 'Los Angeles', label: '로스앤젤레스 (Los Angeles)' },
  { value: 'London', label: '런던 (London)' },
  { value: 'Paris', label: '파리 (Paris)' },
  { value: 'Singapore', label: '싱가포르 (Singapore)' },
  { value: 'Beijing', label: '베이징 (Beijing)' },
  { value: 'Shanghai', label: '상하이 (Shanghai)' },
];

// Language options
const LANGUAGES = [
  { value: 'ko', label: '한국어 (Korean)' },
  { value: 'en', label: 'English' },
  { value: 'ja', label: '日本語 (Japanese)' },
  { value: 'zh', label: '中文 (Chinese)' },
  { value: 'es', label: 'Español (Spanish)' },
  { value: 'fr', label: 'Français (French)' },
  { value: 'de', label: 'Deutsch (German)' },
];

export default function ProfilePage() {
  const { user, logout, setUser } = useAuthStore();
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [name, setName] = useState(user?.name || '');
  const [region, setRegion] = useState(user?.region || 'Seoul');
  const [language, setLanguage] = useState(user?.language || 'ko');

  const handleSave = async () => {
    if (!user) return;

    setIsSaving(true);
    try {
      const updatedUser = await authService.updateProfile({
        name,
        region,
        language,
      });
      setUser(updatedUser);
      toast({
        title: '프로필 업데이트',
        description: '프로필이 업데이트되었습니다.',
      });
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast({
        title: '오류',
        description: '프로필 업데이트에 실패했습니다.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleRegionChange = async (value: string) => {
    if (!user) return;

    setRegion(value);
    try {
      const updatedUser = await authService.updateProfile({ region: value });
      setUser(updatedUser);
      toast({
        title: '지역 변경',
        description: '지역이 변경되었습니다.',
      });
    } catch (error) {
      console.error('Failed to update region:', error);
      setRegion(user.region || 'Seoul');
      toast({
        title: '오류',
        description: '지역 변경에 실패했습니다.',
        variant: 'destructive',
      });
    }
  };

  const handleLanguageChange = async (value: string) => {
    if (!user) return;

    setLanguage(value);
    try {
      const updatedUser = await authService.updateProfile({ language: value });
      setUser(updatedUser);
      toast({
        title: '언어 변경',
        description: '언어가 변경되었습니다.',
      });
    } catch (error) {
      console.error('Failed to update language:', error);
      setLanguage(user.language || 'ko');
      toast({
        title: '오류',
        description: '언어 변경에 실패했습니다.',
        variant: 'destructive',
      });
    }
  };

  const handleLogout = () => {
    logout();
    toast({
      title: '로그아웃',
      description: '성공적으로 로그아웃되었습니다.',
    });
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (!user) {
    return null;
  }

  return (
    <div className="container mx-auto py-8 px-4 max-w-4xl">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <User className="h-8 w-8" />
          프로필
        </h1>
        <p className="text-muted-foreground mt-1">계정 정보를 확인하고 관리합니다.</p>
      </div>

      <div className="space-y-6">
        {/* Profile Card */}
        <Card>
          <CardHeader>
            <CardTitle>기본 정보</CardTitle>
            <CardDescription>프로필 사진과 기본 정보를 관리합니다.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-start gap-6">
              {/* Avatar */}
              <div className="relative">
                <Avatar className="h-24 w-24">
                  <AvatarImage src={user.avatarUrl} alt={user.name} />
                  <AvatarFallback className="text-2xl">{getInitials(user.name)}</AvatarFallback>
                </Avatar>
                <Button
                  size="icon"
                  variant="outline"
                  className="absolute -bottom-2 -right-2 h-8 w-8 rounded-full"
                >
                  <Camera className="h-4 w-4" />
                </Button>
              </div>

              {/* Info */}
              <div className="flex-1 space-y-4">
                {isEditing ? (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">이름</Label>
                      <Input
                        id="name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="이름을 입력하세요"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={handleSave} disabled={isSaving}>
                        {isSaving ? '저장 중...' : '저장'}
                      </Button>
                      <Button variant="outline" onClick={() => setIsEditing(false)} disabled={isSaving}>
                        취소
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-xl font-semibold">{user.name}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>
                            <Shield className="mr-1 h-3 w-3" />
                            {user.role === 'admin' ? '관리자' : '사용자'}
                          </Badge>
                        </div>
                      </div>
                      <Button variant="outline" onClick={() => setIsEditing(true)}>
                        수정
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Account Info */}
        <Card>
          <CardHeader>
            <CardTitle>계정 정보</CardTitle>
            <CardDescription>계정과 관련된 정보입니다.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <Mail className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">이메일</p>
                <p className="font-medium">{user.email}</p>
              </div>
            </div>
            <Separator />
            <div className="flex items-center gap-3">
              <Calendar className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">가입일</p>
                <p className="font-medium">{formatDate(user.createdAt)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Region & Language Settings */}
        <Card>
          <CardHeader>
            <CardTitle>지역 및 언어 설정</CardTitle>
            <CardDescription>AI 챗봇이 사용자의 지역과 언어를 고려하여 응답합니다.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Region */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Globe className="h-4 w-4" />
                  지역
                </Label>
                <Select value={region} onValueChange={handleRegionChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="지역을 선택하세요" />
                  </SelectTrigger>
                  <SelectContent>
                    {REGIONS.map((r) => (
                      <SelectItem key={r.value} value={r.value}>
                        {r.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  선택한 지역의 시간대와 문화적 컨텍스트가 AI 응답에 반영됩니다.
                </p>
              </div>

              {/* Language */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Languages className="h-4 w-4" />
                  언어
                </Label>
                <Select value={language} onValueChange={handleLanguageChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="언어를 선택하세요" />
                  </SelectTrigger>
                  <SelectContent>
                    {LANGUAGES.map((l) => (
                      <SelectItem key={l.value} value={l.value}>
                        {l.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  AI 챗봇이 선택한 언어로 응답합니다.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Danger Zone */}
        <Card className="border-destructive/50">
          <CardHeader>
            <CardTitle className="text-destructive">위험 구역</CardTitle>
            <CardDescription>주의가 필요한 작업입니다.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">로그아웃</p>
                <p className="text-sm text-muted-foreground">현재 세션에서 로그아웃합니다.</p>
              </div>
              <Button variant="destructive" onClick={handleLogout}>
                <LogOut className="mr-2 h-4 w-4" />
                로그아웃
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
